import { useRouter } from 'next/router';

import { Box, Heading, Paragraph } from '@zillow/constellation-10';

import { useCanAccessCondensedFlow } from '../../../../hooks';
import { useGetListingDetails } from '../../../../hooks/queries/properties';
import { useQrPhotoUploadEntryPoint } from '../../../../hooks/useQrPhotoUploadEntryPoint';

import {
  wizardFormColumnStyles,
  wizardFormSectionContainerStyles,
  wizardGridContainerStyles,
  wizardHeadingStyles,
  wizardParagraphStyles,
  wizardTipCardColumnStyles,
} from '../../../../styles/wizard';

import ListingTourFormField from '../../../PostingPath/MediaContent/ListingTourFormField';
import PhotoUploadFormField from '../../../PostingPath/MediaContent/PhotoUploadFormField';
import PageWizard from '../../../Wizard/PageWizard';
import WizardTipCardContainer from '../../../WizardTipCardContainer';

import type { WizardBodyTemplateProps } from '../../../WizardStepController';

const AddPhotos = ({
  onGoToNextStep,
  onGoToPrevStep,
  onExitWizard,
  wizardTitle,
  progressStepper,
  totalSteps,
  currentStepIndex,
  showDesktopViewBreakpoint,
  ...rest
}: WizardBodyTemplateProps) => {
  const router = useRouter();
  const listingAlias = router.query.listingAlias as string;

  const { data: listingDetails } = useGetListingDetails(listingAlias);

  const canAccessCondensedFlow = useCanAccessCondensedFlow();
  const renderPhoneUpload = useQrPhotoUploadEntryPoint(listingAlias);

  const isRoomForRent = !!listingDetails?.roomForRent;
  const photosHeading = isRoomForRent ? 'Add photos of the room and property' : 'Photos';
  const tipHeader = isRoomForRent ? 'Add photos of the room' : 'Add photos';

  return (
    <PageWizard
      {...rest}
      wizardTitle={wizardTitle}
      progressStepper={progressStepper}
      totalSteps={totalSteps}
      currentStepIndex={currentStepIndex}
      onNextClick={onGoToNextStep || undefined}
      onBackClick={onGoToPrevStep || undefined}
      onExitTriggered={onExitWizard}
      showDesktopViewBreakpoint={showDesktopViewBreakpoint}
    >
      {canAccessCondensedFlow && (
        <Heading level={2} textStyle="heading-md" css={wizardHeadingStyles}>
          Media
        </Heading>
      )}
      <Box css={wizardGridContainerStyles}>
        <Box css={wizardFormColumnStyles}>
          <Box css={wizardFormSectionContainerStyles.raw()}>
            <Heading
              level={canAccessCondensedFlow ? 3 : 2}
              textStyle={canAccessCondensedFlow ? 'heading-xs' : 'heading-sm'}
              css={wizardHeadingStyles}
            >
              {photosHeading}
            </Heading>
            <Paragraph css={wizardParagraphStyles}>Photos help renters imagine living in your place.</Paragraph>
            <PhotoUploadFormField emptyStateLayout="panels" renderPhoneUpload={renderPhoneUpload} />
          </Box>

          <Box css={wizardFormSectionContainerStyles.raw()}>
            <Heading level={3} textStyle="heading-sm" css={wizardHeadingStyles}>
              3D tour
            </Heading>
            <Paragraph css={wizardParagraphStyles}>
              Add a 3D tour to give renters a better sense of the home’s layout.
            </Paragraph>
            <ListingTourFormField />
          </Box>
        </Box>
        {isRoomForRent && (
          <Box css={wizardTipCardColumnStyles}>
            <WizardTipCardContainer header={tipHeader} showDesktopViewBreakpoint={showDesktopViewBreakpoint}>
              <Paragraph>
                Renters like to see photos of the room they’ll be renting. Consider adding photos of the room, in
                addition to photos of the property’s shared areas.
              </Paragraph>
            </WizardTipCardContainer>
          </Box>
        )}
      </Box>
    </PageWizard>
  );
};

export default AddPhotos;
